// dialog.js — Dialoge auf nativem <dialog> (Fokusfalle, Esc, Top-Layer gratis).
//   openDialog({ title, body, actions, icon, variant: 'basic'|'fullscreen'|'sheet'|'side', onClose })
//   confirmDialog({ title, text, confirm, cancel, danger }) → Promise<boolean>
// Schließen spielt die Ausblende-Animation, gibt den Fokus zurück und entfernt das Element.
import { html, render } from '../core/h.js';
import { t } from '../../shared/i18n.js';
import { reducedMotion } from '../core/dom.js';
import { springStep, springAtRest } from '../motion/spring-math.js';
import springs from '../tokens/springs.json' with { type: 'json' };

let uid = 0;

export function openDialog({ title = '', body = '', actions = [], icon = null, variant = 'basic', closeLabel = t('ui.close'), onClose, dismissible = true, labelledBy } = {}) {
  const id = 'vcdlg' + (++uid);
  const dlg = document.createElement('dialog');
  dlg.className = 'vc-dialog' + (variant !== 'basic' ? ' is-' + variant : '');
  dlg.setAttribute('aria-labelledby', labelledBy || id + '-t');
  const returnFocus = document.activeElement;
  const primary = actions.find((a) => a.primary);
  render(dlg, html`
    ${variant === 'sheet' ? html`<div class="vc-sheet-handle" data-sheet-handle aria-hidden="true"></div>` : ''}
    <header class="vc-dialog-head">
      ${variant === 'fullscreen' ? html`<span class="vc-dialog-fs-bar">
        <button type="button" class="vc-icon-btn" data-close aria-label="${closeLabel}"><span class="vc-icon" aria-hidden="true">close</span></button></span>` : ''}
      ${icon ? html`<span class="vc-icon" aria-hidden="true">${icon}</span>` : ''}
      ${title ? html`<h2 class="vc-dialog-title" id="${id}-t">${title}</h2>` : ''}
      ${variant === 'fullscreen' && primary ? html`<span class="vc-dialog-fs-bar">
        <button type="button" class="vc-btn is-text" data-act="${actions.indexOf(primary)}">${primary.label}</button></span>` : ''}
    </header>
    <div class="vc-dialog-body"></div>
    ${actions.length ? html`<footer class="vc-dialog-actions">
      ${actions.map((a, i) => html`<button type="button" class="vc-btn ${a.primary ? (a.danger ? 'is-danger' : '') : (a.danger ? 'is-danger is-text' : 'is-text')}" data-act="${i}">${a.label}</button>`)}
    </footer>` : ''}`);
  const bodyEl = dlg.querySelector('.vc-dialog-body');
  if (body instanceof Node) bodyEl.appendChild(body); else render(bodyEl, body);
  document.body.appendChild(dlg);

  let closed = false;
  const api = { el: dlg, body: bodyEl, close };
  function close(result) {
    if (closed) return;
    closed = true;
    const done = () => {
      if (dlg.open) dlg.close();
      dlg.remove();
      if (returnFocus && returnFocus.focus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true });
      if (onClose) onClose(result);
    };
    if (reducedMotion()) return done();
    dlg.classList.add('is-closing');
    dlg.addEventListener('animationend', done, { once: true });
    setTimeout(done, 450);
  }
  dlg.addEventListener('cancel', (e) => { e.preventDefault(); if (dismissible) close(undefined); });
  dlg.addEventListener('click', (e) => {
    if (e.target === dlg && dismissible) {
      const r = dlg.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) close(undefined);
    }
    const c = e.target.closest('[data-close]');
    if (c) close(undefined);
    const a = e.target.closest('[data-act]');
    if (a) {
      const act = actions[Number(a.dataset.act)];
      const res = act && act.onClick ? act.onClick(api) : undefined;
      if (res === false) return; // Aktion hält den Dialog offen (z. B. Validierung)
      if (res && typeof res.then === 'function') {
        a.setAttribute('aria-busy', 'true');
        res.then((v) => { a.removeAttribute('aria-busy'); if (v !== false) close(act.value ?? v); }, () => a.removeAttribute('aria-busy'));
        return;
      }
      close(act ? (act.value ?? true) : undefined);
    }
  });
  dlg.showModal();
  const focusEl = dlg.querySelector('[autofocus], input, select, textarea') || dlg.querySelector('[data-act]:last-child') || dlg;
  if (focusEl && focusEl.focus) focusEl.focus({ preventScroll: true });
  if (variant === 'sheet') attachSheetDrag(dlg, () => close(undefined));
  return api;
}

export function confirmDialog({ title, text, confirm = 'OK', cancel = t('ui.cancel'), danger = false, icon } = {}) {
  return new Promise((resolve) => {
    openDialog({
      title, icon, body: html`<p>${text}</p>`,
      actions: [{ label: cancel, value: false }, { label: confirm, primary: true, danger, value: true }],
      onClose: (v) => resolve(v === true),
    });
  });
}

/** Ziehen am Griff: Feder zurück oder weg (Geschwindigkeit + Strecke). */
export function attachSheetDrag(sheet, onDismiss) {
  const handle = sheet.querySelector('[data-sheet-handle]');
  if (!handle) return;
  const S = springs['spatial-default'];
  let startY = 0, lastY = 0, lastT = 0, v = 0, dragging = false, raf = 0;
  const set = (y) => { sheet.style.transform = y > 0 ? `translateY(${y}px)` : ''; };
  handle.addEventListener('pointerdown', (e) => {
    dragging = true; startY = lastY = e.clientY; lastT = performance.now(); v = 0;
    cancelAnimationFrame(raf); handle.setPointerCapture(e.pointerId); sheet.style.animation = 'none';
  });
  handle.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const now = performance.now();
    v = ((e.clientY - lastY) / Math.max(1, now - lastT)) * 1000;
    lastY = e.clientY; lastT = now;
    const dy = e.clientY - startY;
    set(dy > 0 ? dy : dy / 4);
  });
  const end = () => {
    if (!dragging) return;
    dragging = false;
    const y = Math.max(0, lastY - startY);
    const h = sheet.getBoundingClientRect().height;
    if (y > h * 0.35 || v > 900) { sheet.style.transform = ''; sheet.style.animation = ''; onDismiss(); return; }
    let st = { x: y, v }; let t0 = performance.now();
    const step = (t) => {
      st = springStep(st, 0, S.zeta, S.stiffness, Math.min(0.05, (t - t0) / 1000)); t0 = t;
      set(st.x);
      if (springAtRest(st, 0)) { set(0); return; }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
  };
  handle.addEventListener('pointerup', end);
  handle.addEventListener('pointercancel', end);
}
